/**
 * Cancelling a visit that came from a quote — `docs/visit-page.md` §26.7,
 * Board 17 F (popup 470).
 *
 * Never deleted; it still counts. The popup says so before anything moves:
 * the visit stays on the job as cancelled, the quote's check still reads
 * whole, and its money leaves what the job expects only with a change order —
 * which is why *Tell <salesperson>* is on. *Cancel the visit* waits for *Why*.
 *
 * `POST /api/v1/visits/:id/cancel {reason, tellSalesperson}`. Its entry is the
 * visit header's ⋯ (*Cancel the visit*).
 */
import { useEffect, useState } from 'react'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { visitMark } from '../components/landing-marks.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitPopup, VisitTile } from './sheet.js'
import { CANCEL_VISIT as WORDS } from './copy.js'

interface CancelPreview {
  id: string
  reference: string
  name: string
  state: string
  /** A decimal string at cents — *"1230.00"*. */
  amount: string
  firstDay: string | null
  dayStatus: string | null
  jobId: string
  jobReference: string
  quoteReference: string | null
  quoteVisitNumber: number | null
  check: { expected: number; present: number } | null
  salesperson: { id: string; name: string } | null
}

export function CancelVisit({ visit, onClose, onCancelled }: {
  /** The visit's id or reference. */
  visit: string
  onClose: () => void
  onCancelled?: () => void
}) {
  const [preview, setPreview] = useState<CancelPreview | null>(null)
  const [why, setWhy] = useState('')
  const [tell, setTell] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const phone = usePhone()

  useEffect(() => {
    request<CancelPreview>(`/visits/${encodeURIComponent(visit)}/cancel`)
      .then(setPreview)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
  }, [visit])

  async function cancel() {
    if (!preview || !why.trim()) return
    setBusy(true)
    setError(null)
    try {
      await request(`/visits/${preview.id}/cancel`, {
        method: 'POST', body: { reason: why.trim(), tellSalesperson: tell },
      })
      onCancelled?.()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const buttons = (
    <>
      <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{WORDS.keep}</VisitButton>
      <VisitButton
        size={phone ? 'phone' : 'popup'}
        tone="danger"
        disabled={busy || !why.trim() || !preview}
        onClick={() => void cancel()}
      >
        {WORDS.cancel}
      </VisitButton>
    </>
  )

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={WORDS.title}
      sub={WORDS.sub}
      mark={<VisitTile glyph="close" tone="danger" size={34} />}
      onClose={onClose}
      footer={buttons}
      asDrawn="vp-17 F"
    >
      <div className="flex flex-col gap-3">
        {preview && (
          <div className="flex items-center gap-2.5 rounded-card border border-line-faint bg-surface-alt px-3 py-2.5">
            {visitMark({ id: preview.id, status: preview.dayStatus ?? 'planned', scheduledDate: preview.firstDay }, 28)}
            <span className="flex min-w-0 flex-col">
              <b className="truncate text-[13px]">{preview.name}</b>
              {preview.quoteReference && preview.quoteVisitNumber !== null && (
                <span className="text-[11.5px] text-text-muted">
                  {WORDS.line(preview.reference, preview.quoteReference, preview.quoteVisitNumber, preview.amount)}
                </span>
              )}
            </span>
          </div>
        )}

        <label className="block min-w-0">
          <span className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
            {WORDS.why}
            <i aria-hidden="true" className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />
          </span>
          <textarea
            value={why}
            onChange={(event) => setWhy(event.target.value)}
            autoFocus
            className="block h-14 w-full resize-none rounded-button border border-line bg-surface px-3 py-2.5 text-[13px] leading-normal text-text"
          />
        </label>

        {preview && preview.quoteReference && preview.check && (
          <div className="flex items-start gap-[9px] rounded-card border border-amber-band-line bg-amber-band-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-amber-band-text">
            <VisitGlyph name="info" size={15} stroke={2} />
            <div>
              <b>{WORDS.staysOn(preview.jobReference)}</b>{' '}
              {WORDS.stillCounts(preview.quoteReference, preview.check.present, preview.check.expected)}
            </div>
          </div>
        )}

        {preview?.salesperson && preview.quoteReference && (
          <button
            type="button"
            role="checkbox"
            aria-checked={tell}
            onClick={() => setTell((was) => !was)}
            className="flex items-center gap-2.5 text-left"
          >
            <span className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
              tell ? 'bg-accent text-white' : 'border border-line-strong bg-surface')}>
              {tell && <VisitGlyph name="check" size={12} stroke={3} />}
            </span>
            <span className="text-[12.5px]">
              <b>{WORDS.tell(preview.salesperson.name)}</b> {WORDS.tellWhy}
            </span>
          </button>
        )}

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
